import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { pruefen } from "./kern/pruefliste";
import { postenBilden, type Rechenbasis } from "./kern/rechnen";
import type {
  Beleg,
  Buchung,
  Konto,
  Rechnung,
  Regel,
  Steuerjahr,
  StripeUmsatz,
  Tag,
  TaktZeile,
} from "./kern/typen";

export const sb = () => getSupabaseClient();

export const BUCKET = "bh-belege";

// Fenster sofort öffnen: nach einem await blockiert Safari das Popup
export const belegOeffnen = async (pfad: string) => {
  const fenster = window.open("", "_blank");
  const { data } = await sb().storage.from(BUCKET).createSignedUrl(pfad, 60);
  if (!fenster) return;
  fenster.opener = null;
  if (data?.signedUrl) fenster.location.href = data.signedUrl;
  else fenster.close();
};

export const pruefe = <T extends { error: { message: string } | null }>(
  antwort: T,
) => {
  if (antwort.error) throw new Error(antwort.error.message);
  return antwort;
};

// PostgREST liefert höchstens 1000 Zeilen je Anfrage
const alleSeiten = async <T>(
  seite: (
    von: number,
    bis: number,
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
) => {
  const zeilen: T[] = [];
  for (let von = 0; ; von += 1000) {
    const { data, error } = await seite(von, von + 999);
    if (error) throw new Error(error.message);
    const teil = (data ?? []) as T[];
    zeilen.push(...teil);
    if (teil.length < 1000) return zeilen;
  }
};

export const useZugang = () => {
  const demo =
    import.meta.env.VITE_IS_DEMO === "true" ||
    !import.meta.env.VITE_SUPABASE_URL;
  const { data, isLoading } = useQuery({
    queryKey: ["bh-zugang"],
    enabled: !demo,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const { data: erlaubt, error } = await sb().rpc("bh_zugang");
      return !error && erlaubt === true;
    },
  });
  return { zugang: data === true, isLoading };
};

export type Daten = {
  steuerjahr: Steuerjahr | null;
  jahre: number[];
  takte: TaktZeile[];
  konten: Konto[];
  buchungen: Buchung[];
  regeln: Regel[];
  rechnungen: Rechnung[];
  stripe: StripeUmsatz[];
  tage: Tag[];
  belege: Beleg[];
};

const ladeAlles = async (jahr: number): Promise<Daten> => {
  const von = `${jahr}-01-01`;
  const bis = `${jahr}-12-31`;
  const db = sb();
  const [jahre, takte, konten, regeln, rechnungen, belege, stripe, tage, buchungen] =
    await Promise.all([
      db.from("bh_steuerjahr").select("*").order("jahr"),
      db.from("bh_ustva_takt").select("*").eq("jahr", jahr).order("gueltig_ab"),
      db.from("bh_konto").select("*").order("id"),
      db.from("bh_regel").select("*").order("id"),
      db.from("bh_rechnung").select("*").order("rechnungsdatum"),
    alleSeiten<Beleg>((a, b) => db.from("bh_beleg").select("*").order("id", { ascending: false }).range(a, b)),
      alleSeiten<StripeUmsatz>((a, b) =>
        db
          .from("bh_stripe_umsatz")
          .select("*")
          .gte("datum", von)
          .lte("datum", bis)
          .order("datum")
          .range(a, b),
      ),
      alleSeiten<Tag>((a, b) =>
        db
          .from("bh_tag")
          .select("*")
          .gte("datum", von)
          .lte("datum", bis)
          .order("datum")
          .range(a, b),
      ),
      alleSeiten<Buchung>((a, b) =>
        db
          .from("bh_buchung")
          .select("*")
          .gte("datum", von)
          .lte("datum", bis)
          .order("datum")
          .order("id")
          .range(a, b),
      ),
    ]);
  for (const a of [jahre, takte, konten, regeln, rechnungen]) pruefe(a);
  const zeilen = (jahre.data ?? []) as Steuerjahr[];
  return {
    steuerjahr: zeilen.find((j) => j.jahr === jahr) ?? null,
    jahre: zeilen.map((j) => j.jahr),
    takte: (takte.data ?? []) as TaktZeile[],
    konten: (konten.data ?? []) as Konto[],
    regeln: (regeln.data ?? []) as Regel[],
    rechnungen: (rechnungen.data ?? []) as Rechnung[],
    stripe,
    tage,
    buchungen,
    belege,
  };
};

export const useBuchhaltung = (jahr: number) => {
  const client = useQueryClient();
  const abfrage = useQuery({
    queryKey: ["bh", jahr],
    queryFn: () => ladeAlles(jahr),
  });
  const daten = abfrage.data;
  const rechnung = useMemo(() => {
    if (!daten?.steuerjahr) return null;
    const basis: Rechenbasis = { ...daten, jahr: daten.steuerjahr };
    const posten = postenBilden(basis);
    return { basis, posten, punkte: pruefen(posten, basis) };
  }, [daten]);
  return {
    ...abfrage,
    rechnung,
    neuLaden: () => client.invalidateQueries({ queryKey: ["bh"] }),
  };
};
